import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import axios from 'axios';
import { isValidEmail } from '../../../utilities/validations';
import { FileUploader } from 'react-drag-drop-files';
import { Button, Column, Row, TextInput, List, Alert, Icon, Select } from '@pella/sash-framework';

const ShippingReturnsForm = ({ order, formState }) => {
	const { customer, shipping_address, pqm_order_number } = order?.order || {};
	const { line_1, line_2, city, state, postcode } = shipping_address || {};
	const [values, setValues] = useState({
		name: customer?.name || '',
		address: shipping_address ? `${line_1} ${line_2 ? line_2 + ' ' : ''} ${city}, ${state} ${postcode}` : '',
		email: customer?.email || '',
		orderNumber: pqm_order_number || '',
		reason: '',
		attachment: {},
	});
	const [errors, setErrors] = useState({});
	const [disabled, setDisabled] = useState(false);
	const [success, setSuccess] = useState(false);
	const supportedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'svg', 'tiff'];

	useEffect(() => {
		let attachments = Array.from(values.attachment);

		if (attachments.length > 0) {
			const totalSize = attachments.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024;

			if (totalSize > 10) {
				setErrors((prevErrors) => ({ ...prevErrors, attachment: 'Total upload size is greater than 10MB' }));
				setDisabled(true);
			} else if (totalSize <= 10) {
				setErrors((prevErrors) => ({ ...prevErrors, attachment: '' }));
				setDisabled(false);
			}
		}
	}, [values.attachment]);

	const onInputChange = (e) => {
		setValues({
			...values,
			[e.target.name]: e.target.value,
		});
	};

	const validate = (values) => {
		const errors = {};

		if (!values.name) {
			errors.name = 'Please enter your name';
		}

		if (!values.address) {
			errors.address = 'Please enter your job site address';
		}

		if (!isValidEmail(values.email)) {
			errors.email = 'Please enter a valid email address';
		}

		if (!values.orderNumber) {
			errors.orderNumber = 'Please enter a valid order number';
		}

		if (!values.reason) {
			errors.reason = 'Please enter a reason for return';
		}

		return errors;
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();
		setDisabled(true);

		const errors = validate(values);
		setErrors(errors);

		if (Object.keys(errors).length === 0) {
			const formattedData = {
				name: values.name,
				address: values.address,
				email: values.email,
				orderNumber: values.orderNumber,
				reason: values.reason,
			};

			const formData = new FormData();

			let attachments = Array.from(values.attachment);
			if (attachments.length > 0) {
				attachments.forEach((file) => {
					formData.append('file', file, file.name);
				});
			}

			formData.append('data', JSON.stringify(formattedData));

			// move to actions file?
			try {
				const config = {
					headers: {
						'Content-Type': 'multipart/form-data',
						'Authorization': `Bearer ${process.env.GATSBY_PELLA_API_TOKEN_BEARER}`,
					},
				};

				const response = await axios.post(`${process.env.GATSBY_PELLA_API}/pella-coop/v1/email/shippingreturn`, formData, config);

				if (response.data.success === true) {
					setValues({
						name: '',
						address: '',
						email: '',
						orderNumber: '',
						reason: '',
						attachment: {},
					});
					formState('success');
					setSuccess(true);
					setTimeout(() => {
						document.getElementById('generalContentHeading').scrollIntoView({
							behavior: 'smooth',
						});
					}, '100');
				}
			} catch (error) {
				// add sentry?
			} finally {
				setDisabled(false);
			}
		} else {
			setDisabled(false);
		}
	};

	const handleChange = (files) => {
		setErrors({ ...errors, attachment: '' });
		setValues({ ...values, attachment: files });
	};

	const [isDragging, setIsDragging] = useState(false);

	const removeItemFromFileList = (index) => {
		const newFiles = Array.from(values.attachment);
		newFiles.splice(index, 1);
		setValues({ ...values, attachment: newFiles });
	};

	const formatSizeToMB = (sizeInBytes) => Math.ceil((sizeInBytes / 1024 / 1024) * 100) / 100;

	const handleFileUploadErrors = (error) => {
		setDisabled(true);
		setErrors({ ...errors, attachment: error });
	};

	const isTextWrapping = (text) => {
		const element = document.getElementById(text);
		if (!element) return;

		const elHeight = element?.scrollHeight;
		const computedStyles = window?.getComputedStyle(element);
		const lineHeightPx = computedStyles.getPropertyValue('line-height');
		const lineHeight = parseInt(lineHeightPx.replace('px', ''), 10);
		const lineCount = elHeight / lineHeight;
		return lineCount > 1;
	};

	const uploadComponent = () => {
		return (
			<div className={`flex flex-col items-center border-2 border-dashed border-gray-15 p-6 ${isDragging ? 'bg-gray-10' : 'bg-gray-3'}`}>
				<Icon
					name={isDragging ? 'assignment_add' : 'assignment'}
					size={36}
					className={'mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-10'}
				/>
				{!isDragging && (
					<p>
						<span className='mr-1 hidden md:inline-flex'>Drag and drop files, or</span>
						<Button
							color='tertiary-alt'
							className='!w-fit !p-0'
						>
							Browse
						</Button>
					</p>
				)}

				{isDragging && <p>Drop files to upload</p>}
			</div>
		);
	};

	return (
		<Row>
			{!success && (
				<>
					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='name'
							labelText='Name'
							onChange={(e) => onInputChange(e)}
							required={true}
							value={values.name}
							error={errors.name}
							errorText={errors.name}
							autoComplete='given-name'
						/>
					</Column>
					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='email'
							labelText='Email Address'
							onChange={(e) => onInputChange(e)}
							value={values.email}
							required={true}
							error={errors.email}
							errorText={errors.email}
							autoComplete='email'
						/>
					</Column>
					<Column xs={12}>
						<TextInput
							id='address'
							labelText='Job Site Address'
							onChange={(e) => onInputChange(e)}
							value={values.address}
							required={true}
							error={errors.address}
							errorText={errors.address}
							autoComplete='street-address'
						/>
					</Column>
					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='orderNumber'
							labelText='Order Number'
							onChange={(e) => onInputChange(e)}
							value={values.orderNumber}
							required={true}
							error={errors.orderNumber}
							errorText={errors.orderNumber}
							autoComplete='off'
						/>
					</Column>
					<Column
						xs={12}
						md={6}
					>
						<Select
							id='reason'
							labelText='Reason for Return'
							value={values.reason}
							onChange={onInputChange}
							error={errors.reason ? true : false}
							errorText={errors.reason}
							required
						>
							<option value=''>Select Option</option>
							<option value='I received an incorrect product'>I received an incorrect product</option>
							<option value='My order was damaged during shipping'>My order was damaged during shipping</option>
							<option value='I made a mistake (ordered wrong product, no longer needed, etc.)'>I made a mistake (ordered wrong product, no longer needed, etc.)</option>
						</Select>
					</Column>
					{values.reason?.includes('I made a mistake') && (
						<Column xs={12}>
							<Alert
								type='error'
								className='mb-4'
								hideClose
							>
								We do not accept returns of undamaged made-to-order products. See <Link to='/support/e-commerce-purchase-terms-conditions/'>Purchase Terms & Conditions</Link> for more details.
							</Alert>
						</Column>
					)}
					<Column
						xs={12}
						className='mb-4 [&_input]:max-w-full'
					>
						<p className='text-body-2 text-black'>
							<span className='font-semibold'>Photo Upload:</span> Including a photo of the product you wish to replace is helpful, and a customer service agent will contact you if needed.
						</p>
						<List className='text-body-2 text-black'>
							<li>Uploads are limited to 10 MB total.</li>
							<li>Supported file types include: {supportedFileTypes.join(', ')}.</li>
						</List>

						<FileUploader
							handleChange={handleChange}
							name='file'
							multiple={true}
							hoverTitle={''}
							types={supportedFileTypes}
							// eslint-disable-next-line react/no-children-prop -- required for FileUploader
							children={uploadComponent()}
							maxSize={10}
							dropMessageStyle={{ display: 'none' }}
							onDraggingStateChange={(dragging) => setIsDragging(dragging)}
							onTypeError={() => handleFileUploadErrors(`File type is not supported. Supported file types include: ${supportedFileTypes.join(', ')}.`)}
							onSizeError={() => handleFileUploadErrors('Upload size is too big. All files combined cannot exceed 10 MB.')}
						/>

						{errors.attachment && (
							<Alert
								type='error'
								className='my-4'
								hideClose
							>
								{errors.attachment}
							</Alert>
						)}

						{values.attachment.length > 0 && (
							<List bullets='none'>
								{Array.from(values.attachment).map((file, index) => (
									<li
										key={index}
										className='text-caption mt-3 flex items-center justify-between text-gray-60'
									>
										<p
											id={`filename-${index}`}
											className={isTextWrapping(`filename-${index}`) ? 'relative line-clamp-1' : ''}
										>
											{!isTextWrapping(`filename-${index}`) && file.name}
											{isTextWrapping(`filename-${index}`) && (
												<>
													<span className='absolute right-0 mr-4 bg-white'>{`\u2026 ${file.name.slice(-6)}`}</span>
													<span>{file.name}</span>
												</>
											)}
										</p>

										<div className='flex items-center'>
											<p className='text-tiny text-gray-60'>{`${formatSizeToMB(file?.size)}MB`}</p>
											<Button
												color='tertiary-none'
												onClick={() => removeItemFromFileList(index)}
											>
												<Icon
													name='delete'
													size={21}
													className='ml-4 text-black'
												/>
											</Button>
										</div>
									</li>
								))}
							</List>
						)}
					</Column>
					<Column
						xs={12}
						className='flex'
					>
						<Button
							color='primary-alt'
							type='submit'
							disabled={disabled}
							onClick={(e) => onFormSubmit(e)}
							className='ml-auto'
						>
							Submit
						</Button>
					</Column>
				</>
			)}
			{success && (
				<div
					className='w-full px-4'
					id='returnFormConfirmation'
				>
					<Column
						xs={12}
						className='flex flex-col items-center bg-gray-5 px-8 py-14 md:p-14'
					>
						<div className='mb-5 flex items-center justify-center md:mb-3'>
							<Icon
								name='hotel_class'
								size={48}
								className='mr-2 text-gray-60'
							/>
							<h3 className='m-0'>Thank you!</h3>
						</div>
						<p className='max-w-[33ch] text-center text-black'>You will receive an email with confirmation of your refund or instructions on how to return your product.</p>
					</Column>
				</div>
			)}
		</Row>
	);
};

ShippingReturnsForm.propTypes = {
	order: PropTypes.object,
	formState: PropTypes.func,
};

export default ShippingReturnsForm;
